import { useEffect, useMemo } from 'react';

import { useAgentSetupContext } from 'contexts/AgentSetupContext';

import type { AgentSetupStatusResponse } from 'api/resources/agentWizard/types';
import useProjectRoute from 'hooks/useProjectRoute';

const ACCEPTABLE_ERROR_THRESHOLD = 3;

interface AgentSetupState {
  status: AgentSetupStatusResponse;
  retryCount: number;
  isLoading: boolean;
  hasUnacceptableErrors: boolean;
}

export interface UseAgentSetupReturn {
  hasInitiallyChecked: boolean;
  retry: () => void;
  setupStatus: AgentSetupStatusResponse | null;
  // Convenience accessors
  isLoading: boolean;
  hasUnacceptableErrors: boolean;
  retryCount: number;
  error: string | undefined;
  agentSetupResult: AgentSetupStatusResponse['result'] | undefined;
  progress: AgentSetupStatusResponse['progress'] | undefined;
  faqs: NonNullable<AgentSetupStatusResponse['result']>['faqs'];
  scenarios: NonNullable<AgentSetupStatusResponse['result']>['scenarios'];
}

// Consumes AgentSetupContext, inits passed project id, computes derived values
export const useAgentSetup = (projectId?: string): UseAgentSetupReturn => {
  const { accountId } = useProjectRoute();

  const {
    setupStatus,
    retryCount,
    hasInitiallyChecked,
    retry,
    initializeAgentSetup,
    cancelSetup,
  } = useAgentSetupContext();

  // Initialize agent setup on mount if projectId is present
  useEffect(() => {
    if (projectId && accountId) {
      void initializeAgentSetup({ projectId });
      return () => cancelSetup();
    }
  }, [projectId, accountId, initializeAgentSetup, cancelSetup]);

  // Compute derived state
  const state = useMemo((): AgentSetupState | null => {
    if (!setupStatus) {
      return null;
    }

    const isLoading =
      setupStatus.status === 'pending' || setupStatus.status === 'processing';

    // Has unacceptable errors if there's an error or too many failures
    const hasUnacceptableErrors: boolean =
      !!setupStatus.error ||
      (setupStatus.status === 'completed' &&
        !!setupStatus.result &&
        !setupStatus.result.success &&
        (setupStatus.result.failures?.length ?? 0) >=
          ACCEPTABLE_ERROR_THRESHOLD);

    return {
      status: setupStatus,
      retryCount,
      isLoading,
      hasUnacceptableErrors,
    };
  }, [setupStatus, retryCount]);

  // Show loading state when no entry yet (before initializeAgentSetup completes)
  const isLoading = state?.isLoading ?? !hasInitiallyChecked;

  return {
    setupStatus,
    hasInitiallyChecked,
    retry,
    isLoading,
    hasUnacceptableErrors:
      hasInitiallyChecked && !!state?.hasUnacceptableErrors,
    retryCount: state?.retryCount ?? 0,
    error: state?.status.error,
    agentSetupResult: state?.status.result,
    progress: state?.status.progress,
    faqs: state?.status.result?.faqs ?? [],
    scenarios: state?.status.result?.scenarios ?? [],
  };
};
